import { Link } from "react-router-dom";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CopyBlocks } from "@/components/CopyBlock";
import { fileUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CheckCircle2, AlertCircle, Download } from "lucide-react";

export const isPhotoAsset = asset => ["photo", "photo_pilot"].includes(asset?.metadata?.family);
export const creativeTypeLabel = asset => isPhotoAsset(asset) ? "Photo asset" : asset?.metadata?.family === "copy_verification" ? "Copy verification" : asset?.module === "ads" ? "Ad creative" : asset?.module === "social" ? "Social post" : "Creative asset";

export function CreativeCard({ asset, onOpen, label, className }) {
  const portrait = asset?.metadata?.format === "portrait";
  return (
    <button type="button" onClick={() => onOpen(asset)} data-testid={`creative-card-${asset.module}-${asset.metadata?.index ?? 0}`} className={cn("group text-left card-lux overflow-hidden", className)}>
      <div className={cn("relative overflow-hidden bg-muted", portrait ? "aspect-[4/5]" : "aspect-square")}>
        <img src={fileUrl(asset.thumbnail_url)} alt={asset.copy_blocks?.find((b) => b.channel === "alt_text")?.text || asset.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" loading="lazy" />
        <span className="absolute left-3 top-3 eyebrow-label !text-[9px] bg-background/80 glass px-2 py-1 rounded">{label || asset.metadata?.family?.replace("_", " ")}</span>
      </div>
      <div className="p-4">
        <div className="text-sm font-medium line-clamp-1">{asset.metadata?.headline || asset.title}</div>
        <div className="text-xs text-muted-foreground mt-1">{isPhotoAsset(asset) ? "Reusable photo" : `${asset.copy_blocks?.length || 0} copy blocks`} · {asset.metadata?.size}</div>
      </div>
    </button>
  );
}

function QaBadges({ qa }) {
  if (!qa) return null;
  const checks = [
    ["Text fits", qa.text_fits !== false],
    [`Contrast ${qa.contrast_ratio ?? ""}:1`, qa.contrast_passes !== false],
    ["Logo present", !!qa.logo_present],
    ["Alt text", !!qa.alt_text_present],
    ["Claims clean", !(qa.prohibited_hits || []).length],
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="creative-qa">
      {checks.map(([label, ok]) => (
        <span key={label} className={cn("inline-flex items-center gap-1 text-[10px] font-mono-ls px-2 py-1 rounded-full border", ok ? "border-gold/40 text-foreground" : "border-destructive/50 text-destructive")}>{ok ? <CheckCircle2 className="h-3 w-3 text-gold" /> : <AlertCircle className="h-3 w-3" />}{label}</span>
      ))}
    </div>
  );
}

export function CreativeDialog({ asset, onClose }) {
  const [tab, setTab] = useState("copy");
  if (!asset) return null;
  const url = fileUrl(asset.file_urls?.[0] || asset.thumbnail_url);
  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[calc(100%-2rem)] p-0 overflow-hidden border-border bg-background max-h-[92vh]" data-testid="creative-dialog">
        <div className="grid md:grid-cols-[1.1fr_1fr] max-h-[92vh]">
          <div className="bg-muted/40 flex items-center justify-center p-4 md:p-8 md:max-h-[92vh]">
            <img src={url} alt={asset.copy_blocks?.find((b) => b.channel === "alt_text")?.text || asset.title} className="max-h-[40vh] md:max-h-[80vh] w-auto rounded-lg shadow-2xl" data-testid="creative-dialog-image" />
          </div>
          <div className="p-5 md:p-7 overflow-y-auto scrollbar-thin md:max-h-[92vh]">
            <div className="eyebrow-label mb-1">{creativeTypeLabel(asset)} · {asset.metadata?.family?.replace("_", " ")} · {asset.metadata?.size}</div>
            <DialogTitle className="font-display text-2xl leading-tight">{asset.metadata?.headline || asset.title}</DialogTitle>
            {asset.module === "social" && !isPhotoAsset(asset) && asset.metadata?.family !== "copy_verification" && <p><Link to={`/generate-post?previous=${encodeURIComponent(asset.id)}`} className="underline">Regenerate with a new photo and fresh copy · review quote</Link></p>}
            {asset.metadata?.composition && <Link to={`/compose?edit=${encodeURIComponent(asset.id)}`} className="underline">Edit a new copy of this post</Link>}
            {asset.metadata?.angle && <p className="text-xs text-muted-foreground mt-1">Angle · {asset.metadata.angle.replace("_", " ")}</p>}
            <div className="mt-4 flex items-center gap-2">
              {!isPhotoAsset(asset) && ["copy", "quality"].map((t) => <button key={t} type="button" onClick={() => setTab(t)} data-testid={`creative-tab-${t}`} className={cn("text-xs px-3 h-8 rounded-full border transition-colors", tab === t ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground")}>{t === "copy" ? "Copy blocks" : "Quality check"}</button>)}
              <a href={url} download target="_blank" rel="noreferrer" className="ml-auto btn-quiet h-8 px-3 text-xs" data-testid="creative-download"><Download className="h-3.5 w-3.5" />PNG</a>
            </div>
            <div className="mt-4">
              {isPhotoAsset(asset) ? <p className="text-sm text-muted-foreground" data-testid="photo-only-description">Photo only. Captions and text overlays are not included in this photo generation. Use this photo in a layout or create a separate social post.</p> : tab === "copy" ? <CopyBlocks blocks={asset.copy_blocks} /> : (
                <div className="space-y-4 text-sm">
                  <QaBadges qa={asset.metadata?.qa} />
                  {asset.metadata?.visual_direction && <div><div className="eyebrow-label mb-1">Visual direction</div><p className="text-muted-foreground">{asset.metadata.visual_direction}</p></div>}
                  {asset.metadata?.image_prompt && <div><div className="eyebrow-label mb-1">Imagery brief</div><p className="text-muted-foreground">{asset.metadata.image_prompt}</p></div>}
                  {asset.metadata?.qa?.claims_rewritten?.length > 0 && <p className="text-xs text-muted-foreground">Rewritten to remove: {asset.metadata.qa.claims_rewritten.join(", ")}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
